import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface DespesasFotografoType {
    id: number;
    description?: string;
    value?: number;
    date?: string;
    observation?: string;
    status?: number;
    vendedorId?: number;
    vendedorName?: string;
    tipoDespesasFotografoId?: number;
    tipoDespesasFotografoName?: string;
    tipoDespesasFotografoIcon?: string;
}

export interface CreateDespesasFotografoType {
    description?: string;
    value?: number;
    date?: string;
    observation?: string;
    empresaId?: number;
    vendedorId?: number;
    tipoDespesasFotografoId?: number;
}

export interface UpdateDespesasFotografoType {
    id: number;
    description?: string;
    value?: number;
    date?: string;
    observation?: string;
    terceiroId?: number;
    tipoDespesasFotografoId?: number;
}

export default function useDespesasFotografo() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listDespesasFotografo, setListDespesasFotografo] = useState<DespesasFotografoType[]>([]);
    const [listDespesasFotografoByEmpresa, setListDespesasFotografoByEmpresa] = useState<DespesasFotografoType[]>([]);
    const [despesasFotografoPageCount, setDespesasFotografoPageCount] = useState<number>(0);
    const [initialListDespesasFotografo, setInitialListDespesasFotografo] = useState<DespesasFotografoType[]>([]);
    const [despesasFotografo, setDespesasFotografo] = useState<DespesasFotografoType>({
        id: 0,
        description: "",
        value: 0,
        date: "",
        observation: "",
        status: 0,
        vendedorId: 0,
        vendedorName: "",
        tipoDespesasFotografoId: 0,
        tipoDespesasFotografoName: "",
        tipoDespesasFotografoIcon: "",
    });

    function createDespesasFotografo(createDespesasFotografoType: CreateDespesasFotografoType): Promise<CreateDespesasFotografoType | null> {
        setLoading(true);
        return api
            .post(`/DespesasFotografo/create`, createDespesasFotografoType)
            .then((response) => {
                const createdDespesasFotografo: CreateDespesasFotografoType = response.data;
                showToast("success", "Despesa criada com sucesso.");
                setLoading(false);
                return createdDespesasFotografo;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar despesa:", error);
                return null;
            });
    }

    function getAllDespesasFotografoPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<DespesasFotografoType[] | null> {
        setLoading(true);
        let url = `/DespesasFotografo/getAllDespesasFotografoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListDespesasFotografo(response.data.result);
                setInitialListDespesasFotografo(response.data.result);
                setDespesasFotografoPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar despesas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar despesas:", error);
                return null;
            });
    }

    function updateDespesasFotografo(updateDespesasFotografoData: UpdateDespesasFotografoType): Promise<UpdateDespesasFotografoType | null> {
        setLoading(true);
        return api
            .put(`/DespesasFotografo/${updateDespesasFotografoData.id}`, updateDespesasFotografoData)
            .then((response) => {
                showToast("success", "Despesa atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar despesa:", error);
                return null;
            });
    }

    function deleteDespesasFotografo(despesasFotografoId: number): Promise<DespesasFotografoType | null> {
        setLoading(true);
        return api
            .delete(`/DespesasFotografo/${despesasFotografoId}`)
            .then(() => {
                setDespesasFotografo({
                    id: 0,
                    description: "",
                    value: 0,
                    date: "",
                    observation: "",
                    status: 0,
                    vendedorId: 0,
                    vendedorName: "",
                    tipoDespesasFotografoId: 0,
                    tipoDespesasFotografoName: "",
                    tipoDespesasFotografoIcon: "",
                });
                showToast("success", "Despesa deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar despesa:", error);
                return null;
            });
    }

    function handleGetDespesasFotografoByEmpresaId(empresaId: number): Promise<DespesasFotografoType[] | null> {
        setLoading(true);
        return api
            .get(`/DespesasFotografo/getDespesasFotografoByEmpresaId?empresaId=${empresaId}`)
            .then((response) => {
                const despesasFotografo: DespesasFotografoType[] = response.data;
                setListDespesasFotografoByEmpresa(despesasFotografo);
                setLoading(false);
                return despesasFotografo;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das despesas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das despesas:", error);
                return null;
            });
    }

    function aprovarDespesasFotografo(despesaId: number): Promise<boolean> {
        setLoading(true);
        return api
            .put(`/DespesasFotografo/${despesaId}/aprovar`)
            .then(() => {
                showToast("success", "Despesa aprovada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao aprovar despesa. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao aprovar despesa:", error);
                return false;
            });
    }

    function rejeitarDespesasFotografo(despesaId: number): Promise<boolean> {
        setLoading(true);
        return api
            .put(`/DespesasFotografo/${despesaId}/rejeitar`)
            .then(() => {
                showToast("success", "Despesa rejeitada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao rejeitar despesa. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao rejeitar despesa:", error);
                return false;
            });
    }



    return {
        createDespesasFotografo,
        getAllDespesasFotografoPaged,
        updateDespesasFotografo,
        deleteDespesasFotografo,
        setListDespesasFotografo,
        listDespesasFotografo,
        setDespesasFotografoPageCount,
        despesasFotografoPageCount,
        setInitialListDespesasFotografo,
        initialListDespesasFotografo,
        setDespesasFotografo,
        despesasFotografo,
        handleGetDespesasFotografoByEmpresaId,
        setListDespesasFotografoByEmpresa,
        listDespesasFotografoByEmpresa,
        aprovarDespesasFotografo,
        rejeitarDespesasFotografo,
    };
}